/* eslint-disable sonarjs/no-duplicate-string */
import { RESTRICTION_OPTIONS } from "../../constants";
import {
  convertDate,
  getFormattedDate,
  getFundingSourceGivenUnstandardizedString,
  sortAlphabeticalLabelSelectOptions,
} from "../../helpers";
import { filterListByKeywordGivenKeyNames } from "./objects";

export const ingestData = (list, param) => {
  if (!list?.[param]) return [];
  return list[param].map((option) => ({
    label: option,
    value: option,
  }));
};

export const ingestTerritoryData = (list) =>
  list.map((el) => ({
    label: el,
    value: el,
  }));

export const ingestAdvisorSponsorData = (list) =>
  list.map((el) => ({
    label: el.split(":")[1],
    value: el.split(":")[0],
  }));

export const ingestSponsorData = (list) => {
  const categories = [];
  const strategyNames = {};
  const strategies = {};
  const clientQuestionnaireVisibleSections = {};

  for (const idx in list) {
    if (list[idx].sector === null) {
      list[idx].sector = "Unassigned";
    }
    const combinedLabel = `${list[idx].assetClass}-${list[idx].sector}`;
    if (categories.indexOf(combinedLabel) < 0) {
      categories.push(combinedLabel);
    }
    if (typeof strategies[list[idx].sector] === "undefined") {
      strategies[list[idx].sector] = [];
      strategyNames[list[idx].sector] = [];
    }

    if (strategyNames[list[idx].sector].indexOf(list[idx].strategyName) < 0) {
      strategyNames[list[idx].sector].push(list[idx].strategyName);
      strategies[list[idx].sector].push({
        label: list[idx].strategyName,
        value: list[idx].strategyCode,
      });
    }
  }

  const sortedCategories = categories
    .map((category) => {
      return {
        label: category.split("-").join(" - "),
        value: category.split("-")[1],
      };
    })
    .sort(sortAlphabeticalLabelSelectOptions);
  const sortedStrategies = {};
  for (const key in strategies) {
    sortedStrategies[key] = strategies[key].sort(
      sortAlphabeticalLabelSelectOptions
    );
  }

  return {
    categories: sortedCategories,
    strategies: sortedStrategies,

    clientQuestionnaireVisibleSections,
  };
};

export const setFormValue = (value, fallback, conversion) => {
  if (value !== null && typeof value !== "undefined") {
    switch (conversion) {
      case "toString":
        return value.toString();
      case "toPercent":
        return (value * 100).toString();
      case "toYN":
        return value ? "Y" : "N";
      default:
        return value;
    }
  }

  if (typeof fallback !== "undefined" && fallback !== null) {
    if (conversion === "toString") return "";
    return fallback;
  }

  return "";
};

export const getStrategyDetailByName = (strategyDetails, strategyName) =>
  strategyDetails?.find((strat) => strat.strategyName === strategyName);

export const getRestrictionValue = (data) => {
  if (data?.restrictBuy) {
    return RESTRICTION_OPTIONS.buy;
  }

  if (data?.restrictSell) {
    return RESTRICTION_OPTIONS.sell;
  }

  if (data?.restrictTrade) {
    return RESTRICTION_OPTIONS.trade;
  }

  if (data?.restrictHold) {
    return RESTRICTION_OPTIONS.hold;
  }

  return RESTRICTION_OPTIONS.none;
};

const parseTemplateExclusion = (exclusions, field) =>
  exclusions
    ?.filter(
      (exclusion) =>
        exclusion.exclusionType ===
          field.charAt(0).toUpperCase() + field.slice(1) ||
        exclusion.exclusionType === field
    )
    ?.map((exclusion) => ({
      [field === "esg_screens" ? "name" : field]: exclusion.exclusion,
      weight: exclusion.weight,
    }));

// eslint-disable-next-line sonarjs/cognitive-complexity
export const processDataToFormValues = (apiData, options) => {
  const data = apiData || {};
  const { strategyName, templateTaxLot, templateSecRestriction } = data;

  const values = {
    client: setFormValue(data.client),
    territory: setFormValue(data.territory),
    advisorSponsor: setFormValue(data.advisorSponsor),
    advisorEmail: setFormValue(data.advisorEmail),
    sendReportToAdvisor: setFormValue(data.sendReportToAdvisor),
    salesforceId: setFormValue(data.salesforceId),
    emailCC: setFormValue(data.emailCC),
    sendReporttoCC: setFormValue(data.sendReporttoCC),
    account: setFormValue(data.account),
    fundingSrc: getFundingSourceGivenUnstandardizedString(data.fundingSrc),
    advisorName: setFormValue(data.advisorName),
    taxState: setFormValue(data.taxState),
    maxRealizedGainShort: setFormValue(data.maxRealizedGainYtd),
    maxRealizedGainLong: setFormValue(data.maxRealizedLTGain),
    taxBudgetPerYear: setFormValue(data.taxBudgetPerYear),
    stTaxRate: setFormValue(data.stTaxRate, null, "toPercent"),
    ltTaxRate: setFormValue(data.ltTaxRate, null, "toPercent"),
    yearsToTransition: setFormValue(data.yearsToTransition, null, "toString"),
    carryFwdLosses: setFormValue(data.carryFwdLosses),
    cashToRaise: setFormValue(data.cashToRaise),
    stYtdRealizedG: setFormValue(data.stYtdRealizedG),
    ltYtdRealizedG: setFormValue(data.ltYtdRealizedG),
    ltYtdRealizedL: setFormValue(data.ltYtdRealizedL),
    stYtdRealizedL: setFormValue(data.stYtdRealizedL),
    yieldPreference: setFormValue(data.yieldSpecialInstructions),
    sectorPreference: setFormValue(data.sectorSpecialInstructions),
    esgPreference: setFormValue(data.esgSpecialInstructions),
    cashAmount: setFormValue(data.cashAmount),
    taxLotData: setFormValue(data.templateTaxLot),
    accountRestrictionData: setFormValue(data.templateSecRestriction),
    securityLimitData: setFormValue(data.templateSecRestriction),
    stateExclusions: setFormValue(
      parseTemplateExclusion(data.templateExclusions, "state") ?? []
    ),
    sectorExclusions: setFormValue(
      parseTemplateExclusion(data.templateExclusions, "sector") ?? []
    ),
    gics_industry_group: setFormValue(
      parseTemplateExclusion(data.templateExclusions, "gics_industry_group") ??
        []
    ),
    esg_screens: setFormValue(
      parseTemplateExclusion(data.templateExclusions, "esg_screens") ?? []
    ),
  };

  // Strategy
  const strategyDetail = getStrategyDetailByName(
    options.strategyDetails,
    strategyName
  );
  values.selectCategory = strategyDetail?.sector;
  values.selectStrategy = strategyDetail?.strategyCode;

  // Tax Lot Group Field
  if (templateTaxLot?.length > 0) {
    values.taxLotData = templateTaxLot.map((lot) => {
      return {
        ...lot,
        purchaseDate: convertDate(lot.purchaseDate),
        cusip: lot.cusip || lot.ticker,
      };
    });
  } else {
    values.taxLotData = [
      {
        cusip: "",
        purchaseDate: "",
        costBasis: "",
        qty: "",
      },
    ];
  }
  //Special Instructions
  if (strategyDetail && strategyDetail.assetClass !== "Equity") {
    // Credit Quality

    values["credit_quality_SI"] = setFormValue(data.credit_quality_SI);

    Object.assign(values, {
      statePreference: setFormValue(data.statePreferenceOption),
      maturityPreference: setFormValue(
        data.maturitySpecialInstructions?.split("-").map(Number)
      ),
    });

    if (values.maturityPreference) {
      if (values.maturityPreference.length === 2) {
        values.maturityPreference[1] =
          values.maturityPreference[1] ||
          Number(strategyDetail?.maturityUpperBound);
      }
      values.maturityPreference[0] =
        values.maturityPreference[0] ||
        Number(strategyDetail?.maturityLowerBound);
    }
  }

  // Security Restriction Group Field
  if (templateSecRestriction?.length > 0) {
    values.accountRestrictionData = [];
    values.securityLimitData = [];
    templateSecRestriction.forEach((lot) => {
      lot.restrictBuy === null
        ? (lot.minExposure || lot.maxExposure) &&
          values.securityLimitData.push({
            ...lot,
            cusip: lot.cusip || lot.ticker,
          })
        : values.accountRestrictionData.push({
            ...lot,
            cusip: lot.cusip || lot.ticker,
            restriction: getRestrictionValue(lot),
          });
    });
  } else {
    values.accountRestrictionData = [];
    values.securityLimitData = [
      {
        cusip: "",
        minExposure: "",
        maxExposure: "",
      },
    ];
  }

  return values;
};

export const hasFilledAllRequired = (values) =>
  values.every((value) => typeof value !== "undefined" && value !== "");

export const getUserSessionProposals = () => {
  const userSessionProposals = sessionStorage.getItem("user-session-proposals");
  return userSessionProposals !== null ? JSON.parse(userSessionProposals) : [];
};

export const parseValue = (v) => {
  return typeof v === "string" ? parseFloat(v.replace(/,/g, "")) : v;
};

export const removeSymbol = (v, prefix) => {
  return isNaN(v) ? v.replace(prefix, "") : v;
};

export const handleInputPrefix = (value, prefix) => {
  return value ? prefix : "";
};

export const maxLengthValidation = (limit, message) => {
  return {
    value: limit,
    message: message || `Maximum ${limit} character allowed`,
  };
};

export const validateYesNo = (v) => {
  return (
    ((typeof v !== "undefined" || v !== null) &&
      (v.toLowerCase() === "y" || v.toLowerCase() === "n" || v === "")) ||
    "Y or N only"
  );
};

export const getTaxLotDataInputFields = ({ isFormLocked }) => {
  const isLocked = isFormLocked;
  const requiredFieldMessage = "Required field";
  const maxLengthValidation = {
    value: 16,
    message: "Maximum 16 character allowed",
  };

  const dateToday = new Date();
  const formattedDate = getFormattedDate(dateToday);
  const convertedDate = convertDate(formattedDate);

  return [
    {
      name: "cusip",
      label: "Ticker / CUSIP",
      options: {
        readOnly: isLocked,
        placeholder: "Enter ticker / CUSIP",
        required: requiredFieldMessage,
        setValueAs: (v) => (v?.trim().length > 0 ? v : ""),
        maxLength: maxLengthValidation,
      },
    },
    {
      type: "date",
      name: "purchaseDate",
      label: "Purchase date",
      options: {
        readOnly: isLocked,
        placeholder: convertedDate,
        maxLength: maxLengthValidation,
        setValueAs: (v) => (v.length > 0 ? v : ""),
      },
    },
    {
      name: "costBasis",
      label: "Cost basis",
      options: {
        readOnly: isLocked,
        placeholder: "Defaults to 0/par, if empty",
        setValueAs: (v) => (!isNaN(parseInt(v)) ? parseValue(v) : ""),
        masking: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
          noImmediatePrefix: true,
        },
        maxLength: maxLengthValidation,
      },
    },
    {
      name: "qty",
      label: "Quantity/Units",
      options: {
        readOnly: isLocked,
        placeholder: "Enter numeric value",
        required: requiredFieldMessage,
        setValueAs: (v) => (!isNaN(parseInt(v)) ? parseValue(v) : ""),
        masking: {
          numeral: true,
          numeralThousandsGroupStyle: "thousand",
        },
        maxLength: maxLengthValidation,
      },
    },
  ];
};

export const getAccountRestrictionDataInputFields = ({ isFormLocked }) => {
  const isLocked = isFormLocked;
  const requiredFieldMessage = "Required field";
  const maxLengthValidation = {
    value: 16,
    message: "Maximum 16 character allowed",
  };

  return [
    {
      name: "cusip",
      label: "Ticker / CUSIP",
      options: {
        readOnly: isLocked,
        placeholder: "Enter ticker / CUSIP",
        required: requiredFieldMessage,
        setValueAs: (v) => (v?.trim().length > 0 ? v : ""),
        maxLength: maxLengthValidation,
      },
    },
    {
      type: "radio",
      name: "restriction",
      label: "Restriction",
      options: {
        readOnly: isLocked,
        required: true,
        setValueAs: (v) => (v?.trim().length > 0 ? v : "none"),
        options: [
          {
            label: "Don't buy",
            value: RESTRICTION_OPTIONS.buy,
          },
          {
            label: "Don't sell",
            value: RESTRICTION_OPTIONS.sell,
          },
          {
            label: "Don't trade",
            value: RESTRICTION_OPTIONS.trade,
          },
          {
            label: "Don't hold",
            value: RESTRICTION_OPTIONS.hold,
          },
        ],
      },
    },
  ];
};

export const getSecurityExposureLimitInputFields = ({ isFormLocked }) => {
  const isLocked = isFormLocked;
  const requiredFieldMessage = "Required field";
  const maxLengthValidation = {
    value: 16,
    message: "Maximum 16 character allowed",
  };
  const exposureMaxValidation = {
    value: "100",
    message: "Maximum of 100%",
  };
  const exposureMinValidation = {
    value: "1",
    message: "Minimum of 1%",
  };

  return [
    {
      name: "cusip",
      label: "Ticker / CUSIP",
      options: {
        readOnly: isLocked,
        placeholder: "Enter ticker / CUSIP",
        required: requiredFieldMessage,
        setValueAs: (v) => (v?.trim().length > 0 ? v : ""),
        maxLength: maxLengthValidation,
      },
    },
    {
      name: "minExposure",
      label: "Min exposure",
      options: {
        readOnly: isLocked,
        placeholder: "Enter percentage",
        setValueAs: (v) => {
          const val = removeSymbol(v, "%");
          return !isNaN(parseInt(val)) ? parseValue(val) : "";
        },
        masking: {
          numeral: true,
          numeralPositiveOnly: true,
          prefix: "%",
          noImmediatePrefix: true,
          tailPrefix: true,
        },
        maxLength: maxLengthValidation,
        min: exposureMinValidation,
        max: exposureMaxValidation,
      },
    },
    {
      name: "maxExposure",
      label: "Max exposure",
      options: {
        readOnly: isLocked,
        placeholder: "Enter percentage",
        setValueAs: (v) => {
          const val = removeSymbol(v, "%");
          return !isNaN(parseInt(val)) ? parseValue(val) : "";
        },
        masking: {
          numeral: true,
          numeralPositiveOnly: true,
          prefix: "%",
          noImmediatePrefix: true,
          tailPrefix: true,
        },
        maxLength: maxLengthValidation,
        min: exposureMinValidation,
        max: exposureMaxValidation,
      },
    },
  ];
};

export const hasCreditQualityCustomization = (data) => {
  const cqSiA = data["cqSiA"]?.split("|")[0].split("=")[1] === "Y";
  const cqSiAA = data["cqSiAA"]?.split("|")[0].split("=")[1] === "Y";
  const cqSiAAA = data["cqSiAAA"]?.split("|")[0].split("=")[1] === "Y";
  const cqSiBBB = data["cqSiBBB"]?.split("|")[0].split("=")[1] === "Y";

  return cqSiA || cqSiAA || cqSiAAA || cqSiBBB;
};

export function getValidCustomizations(proposal) {
  const customizations = [];
  const templateExclusions = proposal?.templateExclusions || [];

  if (hasCreditQualityCustomization(proposal)) {
    customizations.push({
      name: "custCreditQuality",
      label: "Credit Quality",
    });
  }

  if (
    proposal?.maturitySpecialInstructions &&
    proposal.maturitySpecialInstructions !== "N/A"
  ) {
    customizations.push({
      name: "custMaturityRange",
      label: "Maturity Range",
    });
  }

  if (
    proposal?.statePreferenceOption &&
    proposal.statePreferenceOption !== "N/A"
  ) {
    customizations.push({
      name: "custStatePreference",
      label: "State Preference",
    });
  }

  if (
    templateExclusions.length > 0 &&
    templateExclusions.some((item) => item.exclusionType === "Sector")
  ) {
    customizations.push({
      name: "custSectorExclusions",
      label: "Sector Exclusions",
    });
  }

  if (
    templateExclusions.length > 0 &&
    templateExclusions.some((item) => item.exclusionType === "State")
  ) {
    customizations.push({
      name: "custStateExclusions",
      label: "State Exclusions",
    });
  }
  return customizations;
}

export const getFormGroupFieldValues = (value, checkAll) => {
  if (!value) return [];
  return value.filter((val) =>
    !checkAll ? val?.cusip !== "" : Object.values(val).every(Boolean)
  );
};

export const searchProposals = (items, keyword = "") => {
  return filterListByKeywordGivenKeyNames(items, keyword, [
    "name",
    "advisor",
    "requesterEmail",
    "strategy",
    "status",
    "reportId",
    "transitionId",
    "sector",
  ]);
};
